import moment from 'moment-tz';
import {AnyAction} from 'redux';

import {AppDispatchType} from 'app/store/AppStore';

import {
  procedureCreator,
  procedureDeleter,
  procedureDetailByHashidFetcher,
  procedureListFetcher,
  procedureVersionDeleter,
  procedureVersionDetailFetcher,
  procedureVersionsFetcher,
} from 'app/procedures/fetchers';
import {showNotification} from 'app/toasts/slice';
import {DEFAULT_PAGE_SIZE, SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {limitRequests} from 'app/utils/redux';
import * as request from 'app/utils/request';
import {createErrorHandler} from 'app/utils/request';

import {
  ContentType,
  ListResponseType,
  ProcedureSerializedType,
  ProcedureVersionSerializedType,
} from 'sweet-types';

import type {
  ProceduresFetchParamsType,
  ProceduresLocationParamsType,
} from './types';
import type {CreateNewDocProps} from 'app/general/types';

export const RECEIVE_PROCEDURE_LIST = 'RECEIVE_PROCEDURE_LIST';
export const RECEIVE_PROCEDURE_DETAIL = 'RECEIVE_PROCEDURE_DETAIL';
export const REQUEST_PROCEDURE_VERSIONS_DETAIL =
  'REQUEST_PROCEDURE_VERSIONS_DETAIL';
export const RECEIVE_PROCEDURE_VERSIONS_DETAIL =
  'RECEIVE_PROCEDURE_VERSIONS_DETAIL';
export const REQUEST_PROCEDURE_VERSIONS_LIST =
  'REQUEST_PROCEDURE_VERSIONS_LIST';
export const RECEIVE_PROCEDURE_VERSIONS_LIST =
  'RECEIVE_PROCEDURE_VERSIONS_LIST';
export const TOGGLE_ADVANCED_MODE = 'TOGGLE_ADVANCED_MODE';
export const SET_SIDEBAR_LIST_STATE = 'SET_SIDEBAR_LIST_STATE';
export const SET_SIDE_DIAGRAM_STATE = 'SET_SIDE_DIAGRAM_STATE';

export const toggleProcedureAdvancedMode = (): AnyAction => ({
  type: TOGGLE_ADVANCED_MODE,
});

export const setSideDiagramState = (
  procedureId: number,
  closed: boolean
): AnyAction => ({
  type: SET_SIDE_DIAGRAM_STATE,
  procedureId,
  closed,
});
export const receiveProcedureDetail = (item: ProcedureSerializedType) =>
  ({
    type: RECEIVE_PROCEDURE_DETAIL,
    item,
  } as const);

export const setSidebarListState = (
  key: string,
  state: Record<string, unknown>
): AnyAction => ({
  type: SET_SIDEBAR_LIST_STATE,
  key,
  state,
});

export const fetchProceduresByHashid = limitRequests(
  ({hashid}: {hashid: string}) =>
    (
      dispatch: AppDispatchType
    ): Promise<{item: ProcedureSerializedType; type: any}> => {
      return procedureDetailByHashidFetcher(hashid)
        .then((procedure) => dispatch(receiveProcedureDetail(procedure)))
        .catch(
          request.createErrorHandler(
            gt('Could not fetch the procedure.'),
            dispatch,
            {
              '404': (error, dispatch) => {
                dispatch(showNotification('Procedure not found', 'error'));
                throw error;
              },
            }
          )
        );
    }
);

export const searchProcedures = (
  search?: string,
  page_size = 10,
  search_fields = 'name',
  ordering: string = SORT_BY_TYPES.RANK
): Promise<unknown> =>
  procedureListFetcher({
    search,
    page_size,
    search_fields,
    ordering,
  });

export const deleteProcedure =
  (url: string) =>
  (dispatch: AppDispatchType): Promise<unknown> =>
    procedureDeleter(url)
      .then(() =>
        dispatch(showNotification(gt('Procedure moved to Recycle Bin')))
      )
      .catch((error) =>
        request.createErrorHandler(
          gt('Unable to delete procedure'),
          dispatch
        )(error)
      );

export const requestProcedureVersionsDetail = (): AnyAction => ({
  type: REQUEST_PROCEDURE_VERSIONS_DETAIL,
});

export const receiveProcedureVersionsDetail = (
  data: ProcedureVersionSerializedType
): AnyAction => ({
  type: RECEIVE_PROCEDURE_VERSIONS_DETAIL,
  data,
});

export const requestProcedureVersionsList = (): AnyAction => ({
  type: REQUEST_PROCEDURE_VERSIONS_LIST,
});

export const receiveProcedureVersionsList = (
  procedureId: number,
  data: ListResponseType<ProcedureVersionSerializedType>
): AnyAction => ({
  type: RECEIVE_PROCEDURE_VERSIONS_LIST,
  procedureId,
  data,
});

export const fetchProcedureVersions = limitRequests(
  (procedureId: number, page = '1', page_size: number = DEFAULT_PAGE_SIZE) =>
    (dispatch: AppDispatchType) => {
      dispatch(requestProcedureVersionsList());
      const params = {
        version_object_id: procedureId,
        page,
        page_size,
      };
      return procedureVersionsFetcher(params)
        .then((data) =>
          dispatch(receiveProcedureVersionsList(procedureId, data))
        )
        .catch(
          request.createErrorHandler(
            gt('Could not fetch procedure versions.'),
            dispatch
          )
        );
    }
);

export const fetchProcedureVersion = limitRequests(
  (versionId: number) => (dispatch: AppDispatchType) => {
    dispatch(requestProcedureVersionsDetail());
    return procedureVersionDetailFetcher(versionId)
      .then((data) => dispatch(receiveProcedureVersionsDetail(data)))
      .catch(
        request.createErrorHandler(
          gt('Could not fetch procedure version'),
          dispatch
        )
      );
  }
);

export const fetchProcedureLatestVersion =
  (procedureId: number) =>
  (dispatch: AppDispatchType): Promise<unknown> => {
    const errorMessage = gt('Could not fetch the latest version');
    return procedureVersionsFetcher({
      version_object_id: procedureId,
      page_size: 1,
      page: '1',
    })
      .then((response) => {
        if (response.results.length === 0) {
          throw Error(`No latest found for version id ${procedureId}`);
        }

        return response.results[0];
      })
      .catch(request.createErrorHandler(errorMessage, dispatch));
  };

export const deleteProcedureVersion =
  (versionURL: string) =>
  (dispatch: AppDispatchType): Promise<unknown> =>
    procedureVersionDeleter(versionURL).then(
      () => dispatch(showNotification(gt('Deleted version'))),
      request.createErrorHandler(gt("Couldn't delete version"), dispatch, {
        '403': () => dispatch(showNotification(gt("Couldn't delete version"))),
      })
    );

export const loadMoreProcedureVersions =
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  (procedureId: number) => (dispatch: AppDispatchType, getState: any) => {
    const versions = getState().proceduresPage.getIn([
      'detailPage',
      'procedureVersions',
      procedureId.toString(),
    ]);
    const page = versions.get('next');
    return fetchProcedureVersions(procedureId, page)(dispatch);
  };

/* procedures dashboard */
export const REQUEST_PROCEDURES_DASHBOARD_LIST =
  'REQUEST_PROCEDURES_DASHBOARD_LIST';

export const requestProceduresDashboardList = (
  currentFilters: ProceduresLocationParamsType = {}
): AnyAction => ({
  type: REQUEST_PROCEDURES_DASHBOARD_LIST,
  currentFilters: {...currentFilters},
});

export const RECEIVE_PROCEDURES_DASHBOARD_LIST =
  'RECEIVE_PROCEDURES_DASHBOARD_LIST';

export const receiveProceduresDashboardList = (
  query: ProceduresFetchParamsType,
  response: ListResponseType<ProcedureSerializedType>
): AnyAction => ({
  type: RECEIVE_PROCEDURES_DASHBOARD_LIST,
  query,
  response,
});

export const TOGGLE_PROCEDURES_DASHBOARD_RELATION =
  'TOGGLE_PROCEDURES_DASHBOARD_RELATION';

export const toggleProceduresDashboardRelation = (
  relation: ContentType
): AnyAction => ({
  type: TOGGLE_PROCEDURES_DASHBOARD_RELATION,
  relation,
});

export const convertLocationParamsToFetchParams = (
  query: ProceduresLocationParamsType
): ProceduresFetchParamsType => {
  const filters = query.filter != null ? query.filter.split(',') : [];
  return {
    page: query.page != null ? query.page : '1',
    page_size:
      query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE.toString(),
    search: query.search,
    ordering: query.ordering,
    private:
      filters.indexOf('private') > -1
        ? 'true'
        : filters.indexOf('public') > -1
        ? 'false'
        : undefined,
    tag: query.tag != null ? query.tag : undefined,
    team_id:
      query.team_id != null && query.team_id !== '-1'
        ? query.team_id
        : undefined,
    in_no_teams: query.team_id === '-1' ? '1' : undefined,
    exclude_team_id: query.exclude_team_id,
    visible_to_user:
      query.visible_to_user != null ? query.visible_to_user : undefined,
    exclude_id: query.exclude_id,
    filter: query.filter,
    folder: query.folder,
  };
};

export const fetchProcedures =
  (fetchParams: ProceduresFetchParamsType) =>
  (dispatch: AppDispatchType): Promise<unknown> => {
    return procedureListFetcher(fetchParams).then(
      (response) =>
        dispatch(receiveProceduresDashboardList(fetchParams, response)),
      createErrorHandler(gt('Unable to load the procedures'), dispatch)
    );
  };

export const fetchProceduresForDashboard =
  (locationParams: ProceduresLocationParamsType) =>
  (dispatch: AppDispatchType): Promise<unknown> => {
    dispatch(requestProceduresDashboardList(locationParams));
    const fetchParams = convertLocationParamsToFetchParams(locationParams);
    return fetchProcedures(fetchParams)(dispatch);
  };

/* === create new procedure === */

export const createNewProcedure =
  (data: CreateNewDocProps) =>
  (
    dispatch: AppDispatchType // https://twitter.com/dan_abramov/status/770914221638942720
  ): Promise<ProcedureSerializedType> =>
    procedureCreator({
      steps_blocked_at: moment().utc().format(),
      ...data,
    }).catch(
      createErrorHandler(gt('Could not create the procedure.'), dispatch)
    );
